import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import Patient from "./Patient";
import { deleteCookie } from "../lib/utils/cookies";
import { useHospDocData } from "../lib/contexts/HospitalDoctorContext";
import { getCheckInDoctor, getDoctorAvailability } from "../lib/apis/doctor";
import { hitRefreshToken } from "../lib/apis/user";
import {
  checkAvailability,
  getBookingListByAvailabilityId,
} from "../lib/apis/booking";
import {
  Booking,
  DocAvailability,
  Doctor,
  SessionAdjustment,
} from "../lib/utils/types";
import { useInterval } from "../lib/utils/useInterval";
import ProfilePicture from "./ProfilePicture";
import ReactDropdown, { Option } from "react-dropdown";
import useSpeakOngoingQueue, { OngoingAnnouncement } from "../lib/customHooks/useSpeakOngoingQueue";
import AudioPermissionModal from "./AudioPermissionModal";
import { getAudioLanguageFromLocalStorage } from "../lib/utils/speechUtils";
import OverflowString from "./OverflowString";
import { QRCodeSVG } from "qrcode.react";
import { useUserData } from "../lib/contexts/UserContext";

import { getRoomMapping } from "../lib/apis/room";
import { formatDoctorName } from "../lib/utils/formatDoctorName";
import { useTVConfiguration } from "../lib/contexts/TVConfigurationContext";
import { useFeatureFlags } from "../lib/contexts/FeatureFlagContext";
import { IoWarning } from "react-icons/io5";
import { sortQueueBookings, isTransferredPatient } from "../lib/utils/queueSort";

export const smoothScrollTo = (
  target: number,
  duration: number,
  callback?: () => void
): { cancel: () => void } => {
  const start = window.scrollY || window.pageYOffset;
  const distance = target - start;
  let startTime: number | null = null;
  let animationFrameId: number;

  const easeInOutQuad = (t: number, b: number, c: number, d: number) => {
    t /= d / 2;
    if (t < 1) return (c / 2) * t * t + b;
    t--;
    return (-c / 2) * (t * (t - 2) - 1) + b;
  };

  const step = (currentTime: number) => {
    if (!startTime) startTime = currentTime;
    const elapsed = currentTime - startTime;
    const newY = easeInOutQuad(elapsed, start, distance, duration);
    window.scrollTo(0, newY);
    if (elapsed < duration) {
      animationFrameId = requestAnimationFrame(step);
    } else {
      if (callback) callback();
    }
  };

  animationFrameId = requestAnimationFrame(step);

  return {
    cancel: () => cancelAnimationFrame(animationFrameId),
  };
};

const ImprovedRoomCard = ({
  roomName,
  isToggled = false,
  colorTheme = "blue",
}: {
  roomName: string;
  isToggled: boolean;
  colorTheme?: string;
}) => {
  if (!roomName) return null;

  // Define color themes for different doctors
  const colorThemes = {
    blue: {
      gradient: "from-blue-600 via-blue-500 to-blue-800",
      border: "border-blue-600/30",
      compactGradient: "from-blue-600 to-blue-800",
      compactBorder: "border-blue-600/20",
    },
    green: {
      gradient: "from-green-600 via-green-500 to-green-800",
      border: "border-green-600/30",
      compactGradient: "from-green-600 to-green-800",
      compactBorder: "border-green-600/20",
    },
    purple: {
      gradient: "from-purple-600 via-purple-500 to-purple-800",
      border: "border-purple-600/30",
      compactGradient: "from-purple-600 to-purple-800",
      compactBorder: "border-purple-600/20",
    },
    orange: {
      gradient: "from-orange-600 via-orange-500 to-orange-800",
      border: "border-orange-600/30",
      compactGradient: "from-orange-600 to-orange-800",
      compactBorder: "border-orange-600/20",
    },
    teal: {
      gradient: "from-teal-600 via-teal-500 to-teal-800",
      border: "border-teal-600/30",
      compactGradient: "from-teal-600 to-teal-800",
      compactBorder: "border-teal-600/20",
    },
    indigo: {
      gradient: "from-indigo-600 via-indigo-500 to-indigo-800",
      border: "border-indigo-600/30",
      compactGradient: "from-indigo-600 to-indigo-800",
      compactBorder: "border-indigo-600/20",
    },
    pink: {
      gradient: "from-pink-600 via-pink-500 to-pink-800",
      border: "border-pink-600/30",
      compactGradient: "from-pink-600 to-pink-800",
      compactBorder: "border-pink-600/20",
    },
    red: {
      gradient: "from-red-600 via-red-500 to-red-800",
      border: "border-red-600/30",
      compactGradient: "from-red-600 to-red-800",
      compactBorder: "border-red-600/20",
    },
  };

  const theme = colorThemes.blue;

  // Non-toggled (desktop) version
  if (!isToggled) {
    return (
      <div className="mb-6">
        <div
          className={`relative overflow-hidden bg-gradient-to-br ${theme.gradient} rounded-3xl py-3 px-6 shadow-2xl border ${theme.border}`}
        >
          {/* Subtle background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-16 h-16 bg-white rounded-full -mr-8 -mt-8"></div>
            <div className="absolute bottom-0 left-0 w-12 h-12 bg-white rounded-full -ml-6 -mb-6"></div>
          </div>

          <div className="relative flex items-center justify-center">
            {/* Icon container with improved styling */}
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-2 mr-4 shadow-lg border border-white/20">
              <svg
                className="w-8 h-8 text-white drop-shadow-sm"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>

            {/* Text content */}
            <div className="text-center flex-1">
              <p className="text-white/80 text-sm font-medium mb-1 tracking-wide uppercase">
                Current Room
              </p>
              <p className="text-white text-2xl font-bold tracking-tight drop-shadow-sm">
                {roomName}
              </p>
            </div>

            {/* Status indicator */}
            <div className="flex items-center ml-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-full px-3 py-1.5 border border-white/30 shadow-lg">
                <div className="flex items-center">
                  <div className="w-2.5 h-2.5 bg-[#43C588] rounded-full animate-pulse shadow-lg mr-2"></div>
                  <span className="text-white font-semibold text-xs">
                    Active
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom accent line */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
        </div>
      </div>
    );
  }

  // Toggled (mobile/compact) version
  return (
    <div className="mb-4">
      <div
        className={`relative overflow-hidden bg-gradient-to-r ${theme.compactGradient} rounded-2xl p-4 shadow-xl border ${theme.compactBorder}`}
      >
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-16 h-16 bg-white rounded-full -mr-8 -mt-8"></div>
          <div className="absolute bottom-0 left-0 w-12 h-12 bg-white rounded-full -ml-6 -mb-6"></div>
        </div>

        <div className="relative flex items-center">
          {/* Compact icon */}
          <div className="bg-white/20 rounded-xl p-2 mr-3 shadow-md">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>

          {/* Compact text */}
          <div className="text-center flex-1">
            <p className="text-white/70 text-xs font-medium uppercase tracking-wider">
              Current Room
            </p>
            <p className="text-white text-lg font-bold mt-0.5">{roomName}</p>
          </div>

          {/* Compact status */}
          <div className="flex items-center">
            <div className="bg-white/20 backdrop-blur-sm rounded-full px-2.5 py-1 border border-white/30 shadow-md">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse mr-1.5"></div>
                <span className="text-white font-medium text-xs">Active</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const QueueSlide = ({
  mapping_id,
  carouselView,
  isToggled,
  showSelect,
  pageSize,
  setPageSize,
  onOngoingChange,
}: {
  mapping_id: string;
  carouselView: boolean;
  isToggled: boolean;
  showSelect: boolean;
  pageSize: number;
  setPageSize: (size: number) => void;
  onOngoingChange?: (mappingId: string) => void;
}) => {
  const { hospData, allDoctorsData } = useHospDocData();
  const { accessToken, setAccessToken } = useUserData();
  const navigate = useNavigate();

  const [docDetails, setDocDetails] = useState<Doctor>();
  const [docAvail, setDocAvail] = useState<DocAvailability[]>();
  const [SelectedDate, setSelectedDate] = useState(
    moment().format("YYYY-MM-DD")
  );

  const [index, setIndex] = useState<number | undefined>(moment().day() + 1);
  const [inClinicData, setInClinicData] = useState<Booking[]>();
  const [options, setOptions] = useState<Option[]>();
  const [session, setSession] = useState<Option>();
  const [onGoing, setOnGoing] = useState<Booking>();
  const [lastAnnouncedBookingId, setLastAnnouncedBookingId] = useState<string | undefined>(undefined);

  const [roomNames, setRoomNames] = useState<
    Array<{ room_id: string; room_name: string }>
  >([]);
  const { tvConfig } = useTVConfiguration();

  const [sessionDetails, setSessionDetails] = useState<{
    id: string;
    queue_type: string;
  }>();

  const isNameHidden = tvConfig?.patient_name === false;
  const { announce_first_name_tv } = useFeatureFlags();

  const ongoingAnnouncements: OngoingAnnouncement[] = (inClinicData ?? [])
    .filter(
      (item) =>
        item.status === 2 ||
        (item.transferred_doctor_mapping_id === mapping_id &&
          item.transfer_status === 2)
    )
    .map((item) => {
      let name: string;
      if (isNameHidden) {
        name = item.token_number ? `Token ${item.token_number}` : "";
      } else {
        const raw = item.booking_alias || item.full_name || "";
        name = announce_first_name_tv ? (raw.trim().split(/\s+/)[0] ?? "") : raw;
      }
      return {
        bookingId: item.booking_id,
        patientName: name,
        transferCycleId: item.recent_transfer_id ?? undefined,
        room:
          roomNames.length === 1
            ? roomNames[0].room_name
            : item.room_name || undefined,
      };
    });

  const {
    showPermissionModal,
    handleAllowAudio,
    handleCancelAudio,
  } = useSpeakOngoingQueue(
    getAudioLanguageFromLocalStorage(),
    docDetails?.full_name,
    ongoingAnnouncements
  );

  const fetchQueueData = async (availability_id: string) => {
    const inclinic_data = await getBookingListByAvailabilityId(
      availability_id,
      SelectedDate
    );
    console.log("fetch");
    if (inclinic_data?.status === 200) {
      console.log("bookings", inclinic_data.data.result);
      const data: Booking[] = inclinic_data.data.result;

      const enrichedData = data.map((item) => {
        let type = item.booking_mode;
        if (item.comments?.toLowerCase() === "follow-up") {
          type = "followup";
        }
        return {
          ...item,
          booking_mode: type,
        };
      });

      setInClinicData(
        enrichedData.filter((item) => {
          // Membership: belongs to this doctor's queue
          if (item.transferred_doctor_mapping_id !== mapping_id) return false;

          // Statuses we render: waiting, ongoing, transferred-in waiting, or in history/scan
          const statusOk =
            item.status === 1 ||
            item.status === 2 ||
            (item.transfer_status !== undefined && [1, 2, 6, 7, 8, 9].includes(item.transfer_status));
          if (!statusOk) return false;

          // Own patients are scoped to the currently selected session.
          // Transferred-in rows skip the availability check (their availability_id belongs to another doctor).
          const isOwnPatient = item.doctor_mapping_id === item.transferred_doctor_mapping_id;
          if (isOwnPatient && item.availability_id !== availability_id) return false;

          return true;
        })
      );

      const ongoingPatient = enrichedData.find((item) => {
        if (item.transferred_doctor_mapping_id !== mapping_id) return false;
        const isOwnOngoing =
          item.status === 2 && item.availability_id === availability_id;
        const isTransferredOngoing = item.transfer_status === 2;
        return isOwnOngoing || isTransferredOngoing;
      });

      if (ongoingPatient !== undefined) {
        setOnGoing(ongoingPatient);
        // Notify parent when a new ongoing patient appears (debounced by booking_id)
        if (ongoingPatient.booking_id !== lastAnnouncedBookingId) {
          setLastAnnouncedBookingId(ongoingPatient.booking_id);
          if (typeof onOngoingChange === "function") {
            onOngoingChange(mapping_id);
          }
        }

        // BROWSER SPEECH SYNTHESIS
        // if (onGoing?.token_number !== ongoingPatient.token_number) {
        //   console.log("Triggering Speech for:", ongoingPatient.full_name);
        //   const text = `Next scanning patient for ${docDetails?.full_name} is ${ongoingPatient.full_name}`;
        //   speakText(text)
        // }
      }
    } else if (inclinic_data?.status === 403 && accessToken) {
      const refresh_data = await hitRefreshToken();
      if (refresh_data?.status === 200) {
        console.log("Refresh");

        setAccessToken(refresh_data.data.result.access_token);
        const api_data = await getBookingListByAvailabilityId(
          session?.value,
          SelectedDate
        );
        if (api_data?.status === 200) {
          const data: Booking[] = api_data.data.result;
          setInClinicData(
            data.filter((item: Booking) => {
              // Membership: belongs to this doctor's queue
              if (item.transferred_doctor_mapping_id !== mapping_id) return false;

              // Statuses we render: waiting, ongoing, transferred-in waiting, or in history/scan
              const statusOk =
                item.status === 1 ||
                item.status === 2 ||
                (item.transfer_status !== undefined && [1, 2, 6, 7, 8, 9].includes(item.transfer_status));
              if (!statusOk) return false;

              // Own patients are scoped to the currently selected session.
              // Transferred-in rows skip the availability check.
              const isOwnPatient = item.doctor_mapping_id === item.transferred_doctor_mapping_id;
              if (isOwnPatient && item.availability_id !== session?.value) return false;

              return true;
            })
          );
        }
      } else {
        deleteCookie("hospID");
        navigate("/");
      }
    } else setInClinicData(undefined);
  };

  useEffect(() => {
    setSelectedDate(moment().format("YYYY-MM-DD"));
    setIndex(moment().day() + 1);
  }, []);

  useEffect(() => {
    const fetchDocAvailability = async () => {
      const res = await getDoctorAvailability(mapping_id);
      if (res?.status === 200) {
        // console.log(res.data.result);
        setDocAvail(res.data.result.doctor_availability);
        setDocDetails(res.data.result.doctor_details);
      } else if (res?.status === 403 && accessToken) {
        const refresh_data = await hitRefreshToken();
        if (refresh_data?.status === 200) {
          console.log("Refresh");
          setAccessToken(refresh_data.data.result.access_token);
          const res = await getDoctorAvailability(mapping_id);
          if (res?.status === 200) {
            setDocAvail(res.data.result.doctor_availability);
            setDocDetails(res.data.result.doctor_details);
          }
        }
      }
    };
    const fetchRoomMapping = async () => {
      const res = await getRoomMapping(mapping_id);
      if (res?.status === 200) {
        // console.log(res.data.result);
        setRoomNames(res.data.result || []);
      }
    };
    fetchDocAvailability();
    fetchRoomMapping();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapping_id, hospData]);

  useEffect(() => {
    if (docAvail !== undefined) {
      const data: Option[] = docAvail
        .filter((i) => i.day_of_week === index)
        .map((item) => {
          const startTimeMoment = moment(item.start_time, "HH:mm:ss");
          const endTimeMoment = moment(item.end_time, "HH:mm:ss");

          return {
            value: String(item.availability_id),
            label: `${startTimeMoment.format(
              "hh:mmA"
            )} - ${endTimeMoment.format("hh:mmA")}`,
            data: {
              wait_time: item.wait_time.minutes,
              queue_type: item.queue_type,
              max_patients: item.max_patients_per_slot,
            },
            start_time: startTimeMoment, // Assigning moment object
            end_time: endTimeMoment, // Assigning moment object
          };
        })
        .sort((a, b) => {
          // Sort by ascending order of time
          return a.start_time.diff(b.start_time);
        });

      const fetchExtension = async () => {
        let newOptions = data.slice();

        for (let i = 0; i < data.length; i++) {
          try {
            const res = await checkAvailability(data[i].value, SelectedDate);
            console.log("checkAvailability", res?.data.result);

            if (res?.status === 200 && res.data.result.length > 0) {
              const api_data: SessionAdjustment[] = res.data.result;

              // eslint-disable-next-line no-loop-func
              api_data.forEach((apiResult) => {
                if (apiResult.type === "Unavailability") {
                  newOptions = newOptions.filter(
                    (session) => apiResult.availability_id !== session.value
                  );
                } else if (apiResult.type === "Extension") {
                  newOptions = newOptions.map((session) => {
                    if (apiResult.availability_id === session.value) {
                      return {
                        ...session,
                        label: `${moment(
                          apiResult.session_start_time,
                          "HH:mm:ss"
                        ).format("hh:mmA")} - ${moment(
                          apiResult.extended_end_time,
                          "HH:mm:ss"
                        ).format("hh:mmA")}`,
                        data: {
                          ...session.data,
                        },
                      };
                    }
                    return session;
                  });
                }
              });
            } else if (res?.status === 404) {
              console.log(
                "Session ID " + data[i].value + " not found, no changes made."
              );
            } else {
              throw new Error("Unhandled response status: " + res.status);
            }
          } catch (error) {
            console.error("Error fetching session availability: ", error);
          }
        }

        setOptions(newOptions);

        const now = moment();
        // const now = moment().set({ hour: 19, minute: 0 });
        console.log(newOptions);
        const currSession = newOptions
          .filter((item) => {
            const startTime = moment(
              String(item?.label)?.split(" - ")[0],
              "HH:mmA"
            ).subtract(30, "minutes"); // Subtract 30 minutes from start time
            const endTime = moment(
              String(item?.label)?.split(" - ")[1],
              "HH:mmA"
            ).add(30, "minutes"); // Add 30 minutes to end time

            return now.isBetween(startTime, endTime); // Check if current time is within the modified range
          })
          .map((item) => {
            return {
              value: String(item.value),
              label: `${moment(
                String(item?.label)?.split(" - ")[0],
                "HH:mm:ss"
              ).format("hh:mmA")} - ${moment(
                String(item?.label)?.split(" - ")[1],
                "HH:mm:ss"
              ).format("hh:mmA")}`,
              data: {
                wait_time: item.data?.wait_time,
                queue_type: item.data?.queue_type,
              },
            };
          });

        console.log(currSession);
        if (currSession && currSession.length > 0) {
          const updatedSession = newOptions.find(
            (opt) => opt.value === currSession[0].value
          );
          setSession(updatedSession || newOptions[0]);
          setSessionDetails({
            id: String(updatedSession?.value || newOptions[0].value),
            queue_type: String(
              updatedSession?.data?.queue_type || newOptions[0].data?.queue_type
            ),
          });

          fetchQueueData(updatedSession?.value || newOptions[0].value);
        } else if (newOptions.length > 0) {
          setSession(newOptions[0]);
          setSessionDetails({
            id: String(newOptions[0].value),
            queue_type: String(newOptions[0].data?.queue_type),
          });
          fetchQueueData(newOptions[0].value);
        } else {
          console.log("No available sessions found");
          setOptions([]);

          // Optionally, handle the case when all sessions are unavailable or removed
        }
      };

      fetchExtension();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SelectedDate, docAvail]);

  useInterval(async () => {
    if (session?.value) {
      fetchQueueData(session?.value);
    }
  }, 6000);

  // console.log(inClinicData);

  const changeOption = (arg: Option) => {
    setInClinicData(undefined);
    setSession(arg);

    const matched = docAvail?.find(
      (i) => String(i.availability_id) === arg.value
    );
    if (matched) {
      setSessionDetails({
        id: String(matched.availability_id),
        queue_type: matched.queue_type,
      });
      fetchQueueData(String(matched.availability_id));
    }
  };

  const [addtionalCheckIndata, setAdditionalCheckInData] = useState({
    is_paused: false,
    pause_reason: "",
  });

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const getDoctorCheckIn = async () => {
      try {
        const res = await getCheckInDoctor(
          mapping_id,
          SelectedDate,
          session?.value || ""
        );
        if (res?.status === 200) {
          setAdditionalCheckInData({
            is_paused: res.data.result.is_paused,
            pause_reason: res.data.result.pause_reason,
          });
        }
      } catch (error) {
        console.error("Error fetching doctor check-in:", error);
      }
    };

    // Immediately fetch once
    getDoctorCheckIn();

    // Set polling interval (every 3 seconds)
    intervalId = setInterval(() => {
      getDoctorCheckIn();
    }, 6000);

    // Cleanup on unmount or when session changes
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [mapping_id, SelectedDate, session]);



  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollDirection, setScrollDirection] = useState<"down" | "up">("down");

  const [isHolding, setIsHolding] = useState(false); // To track the hold state

  const [isVisible, setIsVisible] = useState(false); // To track visibility

  // Function to smoothly scroll the container
  const smoothScrollTo = (target: number, duration: number) => {
    const start = scrollContainerRef.current?.scrollTop || 0;
    const distance = target - start;
    let startTime: number | null = null;

    const easeInOutQuad = (t: number, b: number, c: number, d: number) => {
      t /= d / 2;
      if (t < 1) return (c / 2) * t * t + b;
      t--;
      return (-c / 2) * (t * (t - 2) - 1) + b;
    };

    const step = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const newScrollY = easeInOutQuad(elapsed, start, distance, duration);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = newScrollY;
      }
      if (elapsed < duration) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step); // Start the animation
  };

  // Calculate the scroll duration based on content height
  const calculateScrollDuration = () => {
    if (scrollContainerRef.current) {
      const contentHeight = scrollContainerRef.current.scrollHeight;
      const visibleHeight = scrollContainerRef.current.clientHeight;
      const scrollableHeight = contentHeight - visibleHeight;

      const duration =
        scrollableHeight * 10 > 3000 ? scrollableHeight * 10 : 3000; // Duration should not be less than 3 seconds
      return duration;
    }
    return 3000; // Default duration if no content is available
  };

  // Intersection Observer to detect when the element is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // When the element enters or exits the viewport
        setIsVisible(entry.isIntersecting);
        if (!entry.isIntersecting) {
          // Reset scroll to the top when it's not visible
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
          }
        }
      },
      { threshold: 0.5 } // Trigger when at least 50% of the component is visible
    );

    if (scrollContainerRef.current) {
      observer.observe(scrollContainerRef.current);
    }

    const currentRef = scrollContainerRef.current;
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return; // If the component is not visible, stop scrolling

    const scrollInterval = setInterval(() => {
      if (isHolding) return; // Skip scrolling if we're holding at top or bottom

      const duration = calculateScrollDuration(); // Get duration based on content height

      if (scrollContainerRef.current) {
        if (scrollDirection === "down") {
          // Scroll to the bottom
          smoothScrollTo(scrollContainerRef.current.scrollHeight, duration);
          setScrollDirection("up");
          setIsHolding(true);
          setTimeout(() => {
            setIsHolding(false);
          }, duration + 2000); // Wait for animation to finish + 2s pause
        } else {
          // Scroll to the top
          smoothScrollTo(0, duration);
          setScrollDirection("down");
          setIsHolding(true);
          setTimeout(() => {
            setIsHolding(false);
          }, duration + 2000); // Wait for animation to finish + 2s pause
        }
      }
    }, 5000);

    // Cleanup interval on component unmount or when visibility changes
    return () => clearInterval(scrollInterval);
  }, [scrollDirection, isHolding, isVisible]);

  // Calculate whether to display patient room names
  const displayPatientRoomname = roomNames.length > 1 && tvConfig?.room_number;

  return (
    <div
      className={`flex  h-[83vh] side_bar ${isToggled ? "flex-col" : "flex-row gap-8"
        }`}
    >
      {!isToggled && (
        <div
          className={`fixed h-[85vh] flex ${pageSize >= 5 ? "" : "items-center"
            }`}
        >
          <div className="bg-[#24222014] rounded-[50px] w-[400px] p-4 flex flex-col justify-center items-center h-max relative">
            {docDetails?.profile_picture.startsWith("http") ? (
              <img
                className={`!w-[80px] !h-[80px] rounded-3xl items-center`}
                src={docDetails?.profile_picture}
                alt="doctor"
              ></img>
            ) : (
              <ProfilePicture
                username={docDetails?.full_name || ""}
                gender={docDetails?.gender}
                className={`!w-[80px] !h-[80px]  !rounded-3xl items-center`}
              />
            )}

            <div className={"flex flex-col items-center justify-center"}>
              <p className="text-black md:text-xl 3xl:text-3xl mt-4">
                {formatDoctorName(docDetails?.full_name, allDoctorsData?.find((d) => d.mapping_id === mapping_id)?.show_name_prefix !== false)}{" "}
              </p>
              <p className="flex  md:text-xs 3xl:text-xl text-[#9A9A9A] mt-1.5 ">
                {docDetails?.qualification}/ {docDetails?.specialization}
              </p>
            </div>
            <div className="flex flex-col items-center justify-between">
              <div className="relative flex flex-row gap-2 items-center justify-center">
                {inClinicData && inClinicData?.length > 0 && showSelect ? (
                  <select
                    name="pageSize"
                    id="pageSize"
                    className={`appearance-none w-[50px] h-[50px] text-lg text-center bg-[#8E8E8E33] rounded-full cursor-pointer ${
                      !isToggled ? "absolute top-[10%] -translate-y-[300%] -right-[40px]" : ""
                    }`}
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))} // Use setPageSize directly
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="15">15</option>
                    <option value="20">20</option>
                  </select>
                ) : (
                  <></>
                )}
                <ReactDropdown
                  options={options ? options : []}
                  onChange={changeOption}
                  value={session}
                  placeholder={"Select a session"}
                  menuClassName="rounded-xl !mt-1 md:text-md 3xl:text-2xl text-center"
                  controlClassName="flex items-center md:text-md 3xl:text-2xl justify-center md:!p-2 3xl:!p-4 !mx-[35px] !mt-2.5 !rounded-[80px] !self-center !bg-transparent !border-2 !border-[#0F3CBE] !font-semibold !text-[#0F3CBE] !cursor-pointer"
                  arrowClassName="!hidden"
                />
              </div>
              <div className="mt-3">
                <QRCodeSVG
                  value={`https://wa.me/${hospData?.hospital_contact_number?.replace(
                    /\D/g,
                    ""
                  )}`}
                  size={80} // Adjust size as needed
                />
              </div>
              <p className="md:text-xs 3xl:text-lg text-[#0F3CBE] font-bold mt-2.5">
                Scan to book your appointment
              </p>

              <OverflowString
                text={hospData?.hospital_name}
                length={30}
                className="uppercase font-bold md:!text-xl mt-4 !text-black !text-center"
              />

              <OverflowString
                text={hospData?.hospital_address}
                length={50}
                className="md:text-xs 3xl:text-xl mt-2 font-semibold !text-center w-[60%] !text-black"
              />
              {/* <h3 className="font-semibold md:text-sm 3xl:text-xl my-2">
                Phone no:{" "}
                {hospData?.hospital_contact_number &&
                hospData?.hospital_contact_number.length > 10
                  ? hospData?.hospital_contact_number.slice(2)
                  : hospData?.hospital_contact_number}
              </h3> */}
              {/* <img src={hospData?.logo} className="!h-[20%] !w-[20%] mb-1" /> */}
            </div>
          </div>
        </div>
      )}

      <div
        className={`flex justify-between items-start my-4 ${
          isToggled ? "w-auto sticky !top-[30px] z-[997] bg-white py-2 " : "flex-col w-[420px] "
        }`}
      >
        {isToggled ? (
          <>
            <div className="flex flex-row">
              {docDetails?.profile_picture.startsWith("http") ? (
                <img
                  className={`!w-[50px] !h-[50px] rounded-lg items-center`}
                  src={docDetails?.profile_picture}
                  alt="doctor"
                ></img>
              ) : (
                <ProfilePicture
                  username={docDetails?.full_name || ""}
                  gender={docDetails?.gender}
                  className={"!w-[50px] !h-[50px] rounded-lg items-center"}
                />
              )}

              <div className={"ml-3 flex flex-col justify-center"}>
                <p className="text-black text-lg text-left">
                  {formatDoctorName(docDetails?.full_name, allDoctorsData?.find((d) => d.mapping_id === mapping_id)?.show_name_prefix !== false)}{" "}
                </p>
                <p className="flex items-start text-xs text-[#9A9A9A]  ">
                  {docDetails?.qualification}/{docDetails?.specialization}
                </p>
              </div>
            </div>
            <div className="relative flex flex-row gap-2 justify-center">
              {inClinicData && inClinicData?.length > 0 && showSelect && (
                <select
                  name="pageSize"
                  id="pageSize"
                  className={`appearance-none w-[50px] h-[50px] text-lg text-center bg-[#8E8E8E33] rounded-full cursor-pointer ${
                    !isToggled ? "absolute top-1/2 -translate-y-1/2 -right-[30px]" : ""
                  }`}
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))} // Use setPageSize directly
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="15">15</option>
                  <option value="20">20</option>
                </select>
              )}
              <ReactDropdown
                options={options ? options : []}
                onChange={changeOption}
                value={session}
                placeholder={"Select a session"}
                menuClassName="rounded-xl !mt-1"
                controlClassName="!p-4 !py-3 !rounded-[80px] !self-center !bg-[#0F3CBE] !text-white !cursor-pointer"
                arrowClassName="!hidden"
              />
            </div>
          </>
        ) : (
          <></>
        )}
      </div>

      <div
        ref={scrollContainerRef}
        className={`${isToggled ? "w-auto flex-1" : "xl:w-[70vw] md:w-[55vw] h-[83vh]"} overflow-y-auto side_bar pb-6 scrollbar-hide`}
        style={{ overflowY: "auto", scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {roomNames.length === 1 && tvConfig?.room_number && (
          <ImprovedRoomCard
            roomName={roomNames[0].room_name}
            isToggled={isToggled}
            colorTheme={docDetails?.specialization}
          />
        )}
        {addtionalCheckIndata.is_paused && (
          <div className="flex items-center justify-center w-full mx-auto mb-3">
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-red-500 to-red-600 text-white shadow-md border border-red-400/30 animate-fadeIn">
              {/* Warning Icon */}
              <IoWarning className="w-5 h-5" />

              {/* Message */}
              <p className="text-xs md:text-sm font-medium leading-snug text-center">
                {addtionalCheckIndata.pause_reason}
              </p>
            </div>
          </div>
        )}

        {inClinicData?.length ? (
          <>
            <div
              className={`flex flex-col ${displayPatientRoomname ? "gap-3 pt-2" : ""
                }`}
            >
              {/* {inClinicData?.filter((item) => item.status === 2).length !==
              0 ? ( */}
              {inClinicData
                ?.filter(
                  (item) =>
                    item.status === 2 ||
                    (item.transferred_doctor_mapping_id === mapping_id &&
                      item.transfer_status === 2)
                )
                .sort((a, b) => sortQueueBookings(a, b, sessionDetails?.queue_type))
                .map((item, index) => (
                  <Patient
                    onGoing
                    key={index}
                    pos={item.token_number}
                    name={item.booking_alias || item.full_name}
                    prn={item.prn_number}
                    queue_type={String(sessionDetails?.queue_type)}
                    type={item.booking_mode}
                    patientKey={index + 1}
                    status={item.status}
                    isToggled={isToggled}
                    hospitalID={hospData?.hospital_id}
                    slotStartTime={item?.booked_slot_start}
                    slotEndTime={item?.booked_slot_end}
                    emergency={item.emergency}
                    showRoomName={tvConfig?.room_number}
                    allotedRoom={
                      displayPatientRoomname ? item?.room_name : undefined
                    }
                    onHold={item.onhold}
                    transferStatus={item.transfer_status}
                    recent_transfer_id={item.recent_transfer_id}
                    isTransferred={isTransferredPatient(item)}
                  />
                ))}
              {/* ) : (
                <Patient notStarted />
              )} */}
            </div>

            <div>
              {(() => {
                const isOngoingRow = (item: Booking) =>
                  item.status === 2 ||
                  (item.transferred_doctor_mapping_id === mapping_id &&
                    item.transfer_status === 2);
                const ongoingCount =
                  inClinicData?.filter(isOngoingRow).length ?? 0;
                const waitingRows =
                  inClinicData?.filter((item) => !isOngoingRow(item)) ?? [];
                return waitingRows.length !== 0 || onGoing ? (
                  waitingRows
                    .sort((a, b) => sortQueueBookings(a, b, sessionDetails?.queue_type))
                    .slice(
                      0,
                      ongoingCount === 1
                        ? pageSize - 1
                        : ongoingCount === 2
                          ? pageSize - 2
                          : pageSize
                    )

                  .map((item, index) => (
                    <Patient
                      key={index}
                      pos={item.token_number}
                      name={item.booking_alias || item.full_name}
                      prn={item.prn_number}
                      type={item.booking_mode}
                      patientKey={index + 1}
                      isToggled={isToggled}
                      hospitalID={hospData?.hospital_id}
                      slotStartTime={item?.booked_slot_start}
                      slotEndTime={item?.booked_slot_end}
                      emergency={item.emergency}
                      showRoomName={tvConfig?.room_number}
                      allotedRoom={
                        displayPatientRoomname ? item?.room_name : undefined
                      }
                      onHold={item.onhold}
                      status={item.status}
                      transferStatus={item.transfer_status}
                      recent_transfer_id={item.recent_transfer_id}
                    isTransferred={isTransferredPatient(item)}
                    />
                  ))
                ) : (
                  <Patient
                    empty
                    text={"No Patients in the clinic"}
                    carousel={carouselView}
                    hospitalID={hospData?.hospital_id}
                    isToggled={isToggled}
                    showRoomName={tvConfig?.room_number}
                  />
                );
              })()}
            </div>
          </>
        ) : (
          <Patient
            empty
            text={"No Patients in the clinic"}
            hospitalID={hospData?.hospital_id}
            carousel={carouselView}
            isToggled={isToggled}
            showRoomName={tvConfig?.room_number}
          />
        )}
      </div>
      <AudioPermissionModal
        isOpen={showPermissionModal}
        onAllow={handleAllowAudio}
        onCancel={handleCancelAudio}
      />
    </div>
  );
};

export default QueueSlide;
